import { useMemo } from 'react';
import { getNumericValue } from '@utils/parameters';
import { classNames } from '@utils/classNames';
import { Icon } from '@components/atoms/Icon';
import { IconType } from '@src/types/IconType';
import { DEFAULT_PARAMETER_BAR_CLOSE_TO_LIMIT_THRESHOLD } from '@src/constants';
import { ParameterDto } from '@src/types/types';

enum BarColor {
  GREEN,
  RED,
  GREY,
  ORANGE,
}

function getIndicatorHeightClass(expanded: boolean, offsetToBottom: boolean) {
  if (expanded) {
    if (offsetToBottom) {
      return 'h-12';
    } else {
      return 'h-4';
    }
  } else {
    if (offsetToBottom) {
      return 'h-6';
    } else {
      return 'h-2.5';
    }
  }
}

function getLabelTranslationClass(expanded: boolean, offsetToBottom: boolean) {
  if (expanded) {
    if (offsetToBottom) {
      return 'translate-y-[54px]';
    } else {
      return 'translate-y-[22px]';
    }
  } else {
    if (offsetToBottom) {
      return 'translate-y-[26px]';
    } else {
      return 'translate-y-[12px]';
    }
  }
}

export const ParameterBar = ({
  parameter,
  expanded,
}: {
  parameter: ParameterDto;
  expanded: boolean;
}) => {
  const { sections, regulationIndicators, valueIndicator } = useMemo(() => {
    const { regulations, value } = parameter;
    const isMoreThanMeasurable = value.startsWith('>');
    const numericValue = getNumericValue(parameter);
    const sortedRegulations = regulations
      ?.sort((a, b) => (b.limit ?? 0) - (a.limit ?? 0))
      .filter((regulation) => regulation.limit !== undefined);
    if (!sortedRegulations?.length) {
      return {
        regulationIndicators: [],
        sections: [
          {
            left: 0,
            right: 100,
            color: BarColor.GREY,
          },
        ],
        valueIndicator: {
          position: 50,
          label: `${value} ${parameter.unit}`,
          color: BarColor.GREEN,
        },
      };
    }
    if (sortedRegulations.length > 2) {
      return {
        regulationIndicators: [],
        sections: [],
      };
    }
    const regulation = regulations[0]!;
    const lowerRegulation = regulations[1];
    const limitTrashold =
      lowerRegulation?.closeToLimitTolerance ??
      regulation.closeToLimitTolerance ??
      DEFAULT_PARAMETER_BAR_CLOSE_TO_LIMIT_THRESHOLD;
    const limitMultiplier = 1 - limitTrashold;
    const limit = regulation.limit!;
    let isCloseToLimit = limit - numericValue <= limitTrashold * limit;
    if (lowerRegulation) {
      isCloseToLimit =
        numericValue < lowerRegulation.limit! &&
        lowerRegulation.limit! - numericValue <=
          limitTrashold * lowerRegulation.limit!;
    }
    if (regulations.length === 1 && numericValue === 0 && limit === 0) {
      return {
        regulationIndicators: [
          {
            position: 0,
            label: `${limit} ${parameter.unit}`,
            regulation: regulation.policyName,
          },
        ],
        sections: [
          {
            left: 0,
            right: 100,
            color: BarColor.GREY,
          },
        ],
        valueIndicator: {
          position: 0,
          label: `${value} ${parameter.unit}`,
          color: BarColor.GREEN,
        },
      };
    }
    if (numericValue < limit) {
      const middleOfBar = 50;
      const lowerLimitPosition =
        lowerRegulation?.limit && (middleOfBar * lowerRegulation.limit) / limit;
      return {
        regulationIndicators: [
          {
            position: middleOfBar,
            label: `${limit} ${parameter.unit}`,
            regulation: regulation.policyName,
          },
          ...(lowerRegulation
            ? [
                {
                  position: lowerLimitPosition!,
                  label: `${lowerRegulation.limit} ${parameter.unit}`,
                  regulation: lowerRegulation.policyName,
                },
              ]
            : []),
        ],
        valueIndicator: {
          position: (middleOfBar * numericValue) / limit,
          color:
            lowerRegulation && numericValue > lowerRegulation.limit!
              ? BarColor.RED
              : isCloseToLimit
                ? BarColor.ORANGE
                : BarColor.GREEN,
          label: `${value} ${parameter.unit}`,
        },
        sections: [
          ...(lowerRegulation
            ? [
                {
                  left: 0,
                  right: isCloseToLimit
                    ? lowerLimitPosition! * limitMultiplier
                    : lowerLimitPosition!,
                  color: BarColor.GREEN,
                },
                {
                  left: lowerLimitPosition,
                  right: middleOfBar,
                  color:
                    numericValue > lowerRegulation.limit!
                      ? BarColor.RED
                      : BarColor.GREY,
                },
              ]
            : [
                {
                  left: 0,
                  right: isCloseToLimit
                    ? middleOfBar * limitMultiplier
                    : middleOfBar,
                  color: BarColor.GREEN,
                },
              ]),
          ...(isCloseToLimit
            ? [
                {
                  left: lowerRegulation
                    ? lowerLimitPosition! * limitMultiplier
                    : middleOfBar * limitMultiplier,
                  right: lowerRegulation ? lowerLimitPosition : middleOfBar,
                  color: BarColor.ORANGE,
                },
              ]
            : []),
          {
            left: middleOfBar,
            right: 100,
            color: BarColor.GREY,
          },
        ],
      };
    } else {
      const totalBarLength =
        numericValue +
        (numericValue - limit) * (isMoreThanMeasurable ? 0.1 : 1);
      const limitPosition = (100 * limit) / totalBarLength;
      const lowerLimitPosition =
        lowerRegulation?.limit &&
        (100 * lowerRegulation.limit) / totalBarLength;
      return {
        regulationIndicators: [
          {
            position: limitPosition,
            label: `${limit} ${parameter.unit}`,
            regulation: regulation.policyName,
          },
          ...(lowerRegulation
            ? [
                {
                  position: lowerLimitPosition!,
                  label: `${lowerRegulation.limit} ${parameter.unit}`,
                  regulation: lowerRegulation.policyName,
                },
              ]
            : []),
        ],
        valueIndicator: {
          position: (100 * numericValue) / totalBarLength,
          label: `${value} ${parameter.unit}`,
          color: BarColor.RED,
        },
        sections: [
          ...(lowerRegulation
            ? [
                { left: 0, right: lowerLimitPosition!, color: BarColor.GREEN },
                {
                  left: lowerLimitPosition!,
                  right: limitPosition,
                  color: BarColor.RED,
                },
              ]
            : [
                {
                  left: 0,
                  right: limitPosition,
                  color: BarColor.GREEN,
                },
              ]),
          {
            left: limitPosition,
            right: 100,
            color: BarColor.RED,
          },
        ],
      };
    }
  }, [parameter]);

  const preprocessedIndicators = regulationIndicators
    .sort((i1, i2) => i1.position - i2.position)
    .map((indicator, index) => {
      const layoutedToRight =
        index === 0 ? indicator.position < 20 : indicator.position < 80;
      const flipped = layoutedToRight === (index === 0);
      return {
        ...indicator,
        layoutedToRight,
        flipped,
      };
    });
  const hasOffsetToBottom =
    preprocessedIndicators.length > 1 &&
    preprocessedIndicators.filter((indicator) => indicator.flipped).length ===
      1;
  return (
    <div
      className={classNames(
        'relative w-full',
        expanded ? 'pb-16 pt-12' : 'py-4',
        hasOffsetToBottom && '!pb-24',
      )}
      data-testid="parameter-bar"
      role="img"
    >
      <div className="relative w-full h-3.5">
        {sections.map((section, index) => (
          <div
            key={index}
            className={classNames(
              'absolute h-[14px] border-grey3light',
              section.left === 0 ? 'rounded-l-full' : 'border-l-2 ',
              section.right === 100 ? 'rounded-r-full' : 'border-r-2',
              section.color === BarColor.GREEN && 'bg-green',
              section.color === BarColor.RED && 'bg-red',
              section.color === BarColor.ORANGE && 'bg-orange',
              section.color === BarColor.GREY && 'bg-grey2',
            )}
            style={{
              left: `${section.left}%`,
              right: `${100 - section.right!}%`,
            }}
          />
        ))}
        {preprocessedIndicators.map((indicator, index, allIndicators) => {
          const layoutedToRight = indicator.layoutedToRight;
          const offsetToBottom =
            indicator.flipped &&
            !allIndicators[
              Math.min(Math.max(1 - index, 0), allIndicators.length - 1)
            ].flipped;
          const indicatorHeightClass = getIndicatorHeightClass(
            expanded,
            offsetToBottom,
          );
          const labelTranslationClass = getLabelTranslationClass(
            expanded,
            offsetToBottom,
          );
          return (
            <>
              <div
                key={index}
                data-testid={`parameter-indicator-${index}`}
                className={classNames(
                  'absolute w-1 rounded-full -translate-x-1/2 translate-y-full bg-black',
                  indicatorHeightClass,
                )}
                style={{
                  left: `${indicator.position}%`,
                  bottom: expanded ? -6 : -2,
                }}
              />
              <div
                className={classNames(
                  'absolute top-full  rounded-full text-grey1 leading-4 text-xs text-nowrap z-40',
                  expanded && 'md:font-bold md:text-base',
                  labelTranslationClass,
                  layoutedToRight ? 'text-start' : 'text-end',
                )}
                style={{
                  right: layoutedToRight
                    ? undefined
                    : `${100 - indicator.position}%`,
                  left: layoutedToRight ? `${indicator.position}%` : undefined,
                }}
              >
                {indicator.label}
                {expanded && (
                  <div className="print:block -mt-1 text-xs">
                    {indicator.regulation}
                  </div>
                )}
              </div>
            </>
          );
        })}
        {valueIndicator && (
          <>
            <Icon
              type={IconType.PositionIndicator}
              className={classNames(
                'absolute -translate-x-1/2 -translate-y-[calc(50%+7px)] w-3 h-3',
                valueIndicator.color === BarColor.GREEN && 'text-green',
                valueIndicator.color === BarColor.ORANGE && 'text-orange',
                valueIndicator.color === BarColor.RED && 'text-red',
              )}
              style={{
                left: `${valueIndicator.position}%`,
              }}
            />
            <div />
            {expanded && (
              <div
                className={classNames(
                  'absolute -translate-x-1/2 -translate-y-[calc(100%+13px)] rounded-full py-1 px-5 text-white text-sm text-nowrap',
                  valueIndicator.color === BarColor.GREEN && 'bg-green',
                  valueIndicator.color === BarColor.ORANGE && 'bg-orange',
                  valueIndicator.color === BarColor.RED && 'bg-red',
                )}
                style={{
                  left: `${valueIndicator.position}%`,
                }}
              >
                {valueIndicator.label}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
